// src/App.js - Super Admin Dashboard
//
// GoTrue-login-cutover task (2026-07-06): the "am I logged in" state now
// comes from a GoTrue session (`supabase.auth.getSession()`/
// `onAuthStateChange`) instead of Firebase's `onAuthStateChanged`. The "is
// this session actually a super admin" check — previously a direct
// `getDocs(query(collection(db,'super_admins'), where('auth_uid','==',uid)))`
// Firestore read straight from the browser — is now a call to the Express
// backend's `GET /api/super-admin/whoami` (reuses the exact same
// `requireSuperAdmin` gate every other super-admin route already uses, so a
// 200 here guarantees every other endpoint will also accept this session).

import React, { useState, useEffect, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { supabase } from './supabaseClient';
import { superAdminFetch } from './utils/superAdminApi';

import LoginPage from './LoginPage';
import SuperAdminDashboard from './SuperAdminDashboard';
import './App.css';

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [superAdminData, setSuperAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const resolveSession = useCallback(async (session) => {
    if (!session) {
      setCurrentUser(null);
      setSuperAdminData(null);
      setError('');
      setLoading(false);
      return;
    }

    // التحقق من أن المستخدم هو Super Admin (عبر الباك إند بدل Firestore مباشرة)
    try {
      const data = await superAdminFetch('/whoami');
      setSuperAdminData({ auth_uid: data.uid });
      setCurrentUser(session.user);
      setError('');
    } catch (err) {
      console.error("Error checking super admin status:", err);
      setError(
        err.status === 403
          ? 'ليس لديك صلاحيات للوصول لهذه اللوحة'
          : 'حدث خطأ أثناء التحقق من الصلاحيات'
      );
      await supabase.auth.signOut();
      setCurrentUser(null);
      setSuperAdminData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => resolveSession(data.session));

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setLoading(true);
      resolveSession(session);
    });

    return () => authListener.subscription.unsubscribe();
  }, [resolveSession]);

  // Protected Route
  const ProtectedRoute = ({ children }) => {
    if (loading) {
      return (
        <div style={styles.loadingContainer}>
          جاري التحقق من الصلاحيات...
        </div>
      );
    }

    if (!currentUser || !superAdminData) {
      return <Navigate to="/" replace />;
    }

    return children;
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        جاري التحميل...
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* صفحة تسجيل الدخول */}
        <Route 
          path="/" 
          element={
            !currentUser ? (
              <>
                {error && (
                  <div style={styles.errorBanner}>
                    {error}
                  </div>
                )}
                <LoginPage />
              </>
            ) : (
              <Navigate to="/dashboard" replace />
            )
          } 
        />

        {/* لوحة التحكم */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <SuperAdminDashboard 
                user={currentUser} 
                superAdminData={superAdminData} 
              />
            </ProtectedRoute>
          } 
        />

        {/* أي رابط غير موجود */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

const styles = {
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: '24px',
    color: '#8be9fd',
    backgroundColor: '#1e1e2f',
  },
  errorBanner: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    padding: '15px',
    backgroundColor: '#ff5555',
    color: 'white',
    textAlign: 'center',
    fontWeight: 'bold',
    zIndex: 9999,
  },
};

export default App;
